import { type ReactNode, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  adminTriggerChecks,
  fetchAdminIncidents,
  fetchAdminMaintenance,
  fetchAdminServices,
} from "../../lib/api";

interface DashboardCounts {
  services: number;
  incidents: number;
  maintenance: number;
}

export function DashboardPage(): ReactNode {
  const { user, logout } = useAuth();
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<DashboardCounts>({
    services: 0,
    incidents: 0,
    maintenance: 0,
  });
  const [triggerState, setTriggerState] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [servicesResult, incidentsResult, maintenanceResult] = await Promise.all([
        fetchAdminServices(),
        fetchAdminIncidents(),
        fetchAdminMaintenance(),
      ]);
      setCounts({
        services: servicesResult.services.length,
        incidents: incidentsResult.incidents.length,
        maintenance: maintenanceResult.maintenance.length,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Dashboard konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleTriggerChecks = async () => {
    try {
      const result = await adminTriggerChecks();
      setTriggerState(result.summary.reason ?? `Erfolgreich: ${result.summary.ok}`);
      await load();
    } catch (err: unknown) {
      setTriggerState(err instanceof Error ? err.message : "Checks konnten nicht gestartet werden");
    }
  };

  useEffect(() => {
    if (triggerState === null) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setTriggerState(null);
    }, 4000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [triggerState]);

  if (loading) {
    return <div className="px-6 py-10 dark:bg-slate-950 dark:text-slate-100">Lade Dashboard…</div>;
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-6xl flex-col gap-6 bg-slate-50 px-6 py-8 dark:bg-slate-950 dark:text-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">Angemeldet als {user?.email}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleTriggerChecks}
            className="rounded bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400"
          >
            Checks jetzt starten
          </button>
          <button
            type="button"
            onClick={logout}
            className="rounded border border-slate-300 bg-white px-4 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            Abmelden
          </button>
        </div>
      </header>

      {error && <p className="rounded bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</p>}

      <section className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm text-slate-500 dark:text-slate-400">Services</p>
          <p className="mt-2 text-3xl font-semibold">{counts.services}</p>
          <Link
            className="text-sm text-blue-600 dark:text-blue-400"
            to={`/${tenant}/admin/services`}
          >
            Services verwalten
          </Link>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm text-slate-500 dark:text-slate-400">Incidents</p>
          <p className="mt-2 text-3xl font-semibold">{counts.incidents}</p>
          <Link
            className="text-sm text-blue-600 dark:text-blue-400"
            to={`/${tenant}/admin/incidents`}
          >
            Incidents verwalten
          </Link>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm text-slate-500 dark:text-slate-400">Wartung</p>
          <p className="mt-2 text-3xl font-semibold">{counts.maintenance}</p>
          <Link
            className="text-sm text-blue-600 dark:text-blue-400"
            to={`/${tenant}/admin/maintenance`}
          >
            Wartung verwalten
          </Link>
        </div>
      </section>

      {triggerState && <p className="text-sm text-slate-700 dark:text-slate-300">{triggerState}</p>}
    </main>
  );
}
